import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { PageIntro, PageSection, MoreLink } from "@/components/site/page";

const URL = "https://akillisurdurulebilirlik.com/yazilar/yesil-mutabakat-rehberi";
const TITLE = "Avrupa Yeşil Mutabakatı — Eleştirel Bir Rehber";
const DESC =
  "Avrupa Yeşil Mutabakatı nedir, Fit for 55 ve CBAM Türkiye'yi nasıl etkiliyor? 2050 iklim nötrlüğü hedefinin tarihi, mekanizmaları, maliyeti ve eleştirileri üzerine kapsamlı bir okuma.";
const PUBLISHED = "2026-09-27";

const FAQ = [
  {
    q: "Avrupa Yeşil Mutabakatı nedir, kısaca?",
    a: "Avrupa Yeşil Mutabakatı (European Green Deal), Avrupa Komisyonu'nun Aralık 2019'da açıkladığı, AB'yi 2050'de dünyanın ilk iklim-nötr kıtası yapmayı hedefleyen büyüme ve dönüşüm stratejisidir. Enerji, sanayi, tarım, ulaşım ve finansı kapsayan onlarca yasal düzenlemenin çatı çerçevesidir.",
  },
  {
    q: "Fit for 55 nedir?",
    a: "Fit for 55, AB'nin 2030'a kadar sera gazı emisyonlarını 1990 seviyesine göre en az %55 azaltma hedefini yasalaştıran mevzuat paketidir. Emisyon Ticaret Sistemi'nin genişletilmesi, 2035'ten itibaren benzinli/dizel yeni otomobil satış yasağı ve karbon sınır vergisi (CBAM) gibi düzenlemeleri içerir.",
  },
  {
    q: "CBAM (Sınırda Karbon Düzenleme Mekanizması) nedir?",
    a: "CBAM, AB'ye ithal edilen karbon yoğun ürünlere (çimento, demir-çelik, alüminyum, gübre, elektrik, hidrojen) uygulanan karbon fiyatıdır. Amaç, AB içindeki üreticinin karbon maliyeti ile ithalatçının maliyetini eşitlemek ve 'karbon kaçağını' önlemektir. Geçiş dönemi Ekim 2023'te başladı; mali yükümlülük 2026'dan itibaren devreye giriyor.",
  },
  {
    q: "Yeşil Mutabakat Türkiye'yi nasıl etkiliyor?",
    a: "Türkiye AB'nin önemli ticaret ortağıdır; ihracatın büyük bölümü AB'ye yapılır. CBAM kapsamındaki sektörlerde (özellikle demir-çelik, çimento, alüminyum) Türk ihracatçılar 2026'dan itibaren karbon maliyetiyle karşılaşıyor. Bu, Türkiye'nin kendi emisyon ticaret sistemini kurması ve 2053 net sıfır hedefini somutlaştırması yönünde baskı yaratıyor.",
  },
  {
    q: "Yeşil Mutabakat'a yönelik eleştiriler neler?",
    a: "Üç ana eleştiri var: (1) Finansman — vaat edilen yatırım paketinin önemli bölümü yeni para değil, mevcut bütçenin yeniden etiketlenmesi. (2) Adalet — dönüşüm maliyeti düşük gelirli hanelere ve kömür bölgelerine orantısız yükleniyor; 'Adil Geçiş Fonu' bu yükün küçük bir kısmını karşılıyor. (3) Korumacılık — CBAM, gelişmekte olan ülkeler açısından yeşil bir ticaret duvarı işlevi görebilir.",
  },
  {
    q: "Yeşil Mutabakat ile Paris Anlaşması arasındaki fark ne?",
    a: "Paris Anlaşması küresel ve gönüllülüğe dayalı bir çerçevedir; her ülke kendi katkısını (NDC) belirler. Yeşil Mutabakat ise AB iç hukukuna dökülmüş, bağlayıcı hedefler ve yaptırımlar içeren bölgesel bir dönüşüm programıdır. Paris 'ne' söyler, Yeşil Mutabakat AB için 'nasıl'ı yasalaştırır.",
  },
];

export const Route = createFileRoute("/yazilar/yesil-mutabakat-rehberi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "yeşil mutabakat, avrupa yeşil mutabakatı, european green deal, fit for 55, cbam, sınırda karbon, karbon vergisi, ab iklim politikası, 2050 iklim nötr, yeşil mutabakat türkiye",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/yazilar/yesil-mutabakat-rehberi"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "tr-TR",
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: personRef(),
          publisher: {
            "@type": "Organization",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
            { "@type": "ListItem", position: 3, name: "Yeşil Mutabakat Rehberi", item: URL },
          ],
        }),
      },
    ],
  }),
  component: YesilMutabakatPage,
});

function YesilMutabakatPage() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazılar", to: "/yazilar" }, { label: "Yeşil Mutabakat Rehberi" }]}
        kicker="Eleştirel Rehber"
        title="Avrupa Yeşil Mutabakatı: Bir kıtanın dönüşüm planı, gerisinin faturası"
        lede={
          <>
            Avrupa Yeşil Mutabakatı 2019'da bir vizyon belgesi olarak doğdu, beş
          yıl içinde AB hukukunun en kapsamlı iklim mevzuatına dönüştü. Bu
          rehber, mutabakatın ne olduğunu, hangi mekanizmalarla çalıştığını ve
          Türkiye gibi ticaret ortaklarının neden bu işin içinde olduğunu
          anlatıyor.
          </>
        }
      />
      <PageSection>
        <article className="max-w-3xl">

      <nav className="mb-12 border border-ink/10 p-5 md:p-6 bg-secondary" aria-label="İçindekiler">
        <h2 className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/60 mb-3">
          İçindekiler
        </h2>
        <ol className="space-y-1.5 text-sm">
          <li><a href="#nedir" className="hover:text-theme-tech">1. Yeşil Mutabakat nedir, kısaca</a></li>
          <li><a href="#tarih" className="hover:text-theme-tech">2. 2019'dan bugüne: vizyondan yasaya</a></li>
          <li><a href="#mekanizmalar" className="hover:text-theme-tech">3. Ana mekanizmalar: Fit for 55, ETS, CBAM</a></li>
          <li><a href="#cbam" className="hover:text-theme-tech">4. CBAM: sınırdaki karbon duvarı</a></li>
          <li><a href="#turkiye" className="hover:text-theme-tech">5. Türkiye için anlamı</a></li>
          <li><a href="#finansman" className="hover:text-theme-tech">6. Finansman sorunu: para nerede?</a></li>
          <li><a href="#elestiriler" className="hover:text-theme-tech">7. Üç temel eleştiri</a></li>
          <li><a href="#ne-yapmali" className="hover:text-theme-tech">8. Ne yapmalı: okuma önerisi</a></li>
          <li><a href="#sss" className="hover:text-theme-tech">9. Sıkça sorulanlar</a></li>
        </ol>
      </nav>

      <div className="prose-content space-y-6 text-base md:text-lg leading-[1.75] text-ink/85">
        <section id="nedir" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            1. Yeşil Mutabakat nedir, kısaca
          </h2>
          <p>
            <strong>Avrupa Yeşil Mutabakatı</strong> (European Green Deal),
            Avrupa Komisyonu'nun 11 Aralık 2019'da açıkladığı, AB'yi{" "}
            <strong>2050'de dünyanın ilk iklim-nötr kıtası</strong> yapmayı
            hedefleyen dönüşüm stratejisidir. Tek bir yasa değil; enerji,
            sanayi, tarım, ulaşım, binalar ve finansı kapsayan onlarca
            düzenlemenin çatı çerçevesidir.
          </p>
          <p>
            Temel iddiası şudur: iklim krizi bir çevre politikası konusu değil,
            ekonominin işletim sisteminin yeniden yazılmasıdır. Bu yüzden
            mutabakat, çevre bakanlıklarının değil ekonomi, ticaret ve
            maliyenin gündemine oturdu.
          </p>
        </section>

        <section id="tarih" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            2. 2019'dan bugüne: vizyondan yasaya
          </h2>
          <p>
            Mutabakatın yol haritası üç aşamada okunabilir. <strong>2019–2020:</strong>{" "}
            vizyon belgesi ve Avrupa İklim Yasası — 2050 iklim nötrlüğü hedefi
            bağlayıcı hale geldi. <strong>2021–2023:</strong> Fit for 55 paketi —
            2030 için %55 emisyon azaltımı hedefi yasalaştı, Emisyon Ticaret
            Sistemi (ETS) genişledi, CBAM kabul edildi. <strong>2024 sonrası:</strong>{" "}
            uygulama dönemi — CBAM'ın mali yükümlülüğü 2026'da devreye giriyor,
            2035'te benzinli/dizel yeni otomobil satışı yasaklanıyor.
          </p>
          <p>
            Bu hız dikkat çekicidir: bir vizyon belgesinin beş yıl içinde
            bağlayıcı mevzuata dönüşmesi, AB hukuk tarihinde nadirdir. Sebep
            yalnızca iklim bilinci değil; enerji güvenliği (Rusya-Ukrayna
            savaşı sonrası fosil yakıt bağımlılığını kırma isteği) ve sanayi
            rekabeti (temiz teknolojide Çin ve ABD ile yarış) de aynı yöne
            itti.
          </p>
        </section>

        <section id="mekanizmalar" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            3. Ana mekanizmalar: Fit for 55, ETS, CBAM
          </h2>
          <p>
            Mutabakatın dişlileri üç mekanizma üzerinden döner:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Fit for 55:</strong> 2030'a kadar emisyonları 1990'a göre
              en az %55 azaltma hedefini yasalaştıran mevzuat paketi. Enerji
              verimliliği, yenilenebilir enerji hedefleri ve bina standartları
              bu paketin parçası.
            </li>
            <li>
              <strong>ETS (Emisyon Ticaret Sistemi):</strong> AB'nin karbon
              piyasası. Büyük sanayi ve enerji tesisleri emisyon izni almak
              zorunda; izin fiyatı piyasada oluşur. Fit for 55 ile deniz
              taşımacılığı dahil edildi, bina ve karayolu yakıtları için ikinci
              bir sistem (ETS2) kuruldu.
            </li>
            <li>
              <strong>CBAM (Sınırda Karbon Düzenleme Mekanizması):</strong>{" "}
              AB'ye ithal edilen karbon yoğun ürünlere uygulanan karbon fiyatı.
              İçerideki üreticinin ödediği karbon maliyetini ithalatçıya da
              uygulayarak "karbon kaçağını" önlemeyi amaçlar.
            </li>
          </ul>
          <p>
            Karbon piyasalarının nasıl çalıştığına dair kavramsal arka plan için:{" "}
            <Link to="/cop31" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              COP31 Analiz Katmanı
            </Link>{" "}
            altındaki karbon piyasası notlarına bakılabilir.
          </p>
        </section>

        <section id="cbam" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            4. CBAM: sınırdaki karbon duvarı
          </h2>
          <p>
            CBAM, mutabakatın en tartışmalı aracıdır. Kapsamındaki ürünler:
            çimento, demir-çelik, alüminyum, gübre, elektrik ve hidrojen.
            Ekim 2023'te başlayan geçiş döneminde ithalatçılar yalnızca
            ürünlerin gömülü emisyonunu raporluyordu; <strong>2026'dan
            itibaren</strong> bu emisyonlar için sertifika satın almak
            gerekiyor — fiyat, AB ETS fiyatına endeksli.
          </p>
          <p>
            Mantık basit: AB içindeki bir çelik üreticisi ton başına karbon
            bedeli öderken, aynı çeliği karbonsuz üretim yükümlülüğü olmayan
            bir ülkeden ithal etmek haksız rekabet yaratır. CBAM bu farkı
            kapatır. Ama aynı mantık, gelişmekte olan ülkeler açısından{" "}
            <em>yeşil bir ticaret duvarı</em> olarak da okunur — bu eleştiriye
            7. bölümde döneceğiz.
          </p>
        </section>

        <section id="turkiye" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            5. Türkiye için anlamı
          </h2>
          <p>
            Türkiye AB ile Gümrük Birliği içindedir ve ihracatının büyük
            bölümünü AB'ye yapar. CBAM kapsamındaki sektörler — özellikle{" "}
            <strong>demir-çelik, çimento ve alüminyum</strong> — Türkiye'nin
            AB ihracatında önemli paya sahiptir. 2026'dan itibaren bu
            sektörlerdeki ihracatçılar, ürünlerinin gömülü emisyonu kadar
            karbon maliyetiyle karşılaşıyor.
          </p>
          <p>
            Bu baskı Türkiye'yi iki yönde harekete geçirdi: ulusal bir{" "}
            <strong>emisyon ticaret sistemi</strong> kurma çalışmaları
            hızlandı ve 2053 net sıfır hedefi somut politikalarla
            desteklenmeye başlandı. CBAM'ın ironik etkisi şudur: AB'nin iç
            politikası, Türkiye'nin iklim mevzuatını Brüksel'den daha hızlı
            şekillendiriyor.
          </p>
        </section>

        <section id="finansman" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            6. Finansman sorunu: para nerede?
          </h2>
          <p>
            Mutabakatın başında açıklanan yatırım paketi etkileyici görünüyordu.
            Ama bağımsız analizler, vaat edilen tutarın önemli bölümünün yeni
            para değil, mevcut AB bütçesinin ve ulusal katkıların{" "}
            <em>yeniden etiketlenmesi</em> olduğunu gösterdi. Pandemi sonrası
            kurulan kurtarma fonu (NextGenerationEU) yeşil harcamaları
            desteklese de, dönüşümün gerçek faturası — yıllık yüz milyarlarca
            avro ek yatırım — büyük ölçüde üye devletlerin ve özel sektörün
            sırtında.
          </p>
          <p>
            Bu boşluk, mutabakatın en zayıf halkasıdır: hedefler yasayla
            bağlayıcı, finansman ise büyük ölçüde temenni. İklim
            finansmanının küresel boyutu için:{" "}
            <Link to="/cop31" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              COP31 Analiz Katmanı
            </Link>.
          </p>
        </section>

        <section id="elestiriler" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            7. Üç temel eleştiri
          </h2>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Adalet eleştirisi:</strong> Dönüşümün maliyeti orantısız
              dağılıyor. Enerji fiyatlarındaki artış düşük gelirli haneleri,
              kömür bölgelerindeki istihdam kaybı ise dar bir coğrafyayı
              vuruyor. Adil Geçiş Fonu bu yükün ancak küçük bir bölümünü
              karşılıyor. "Kim ödüyor?" sorusu mutabakatın zayıf karnı.
            </li>
            <li>
              <strong>Korumacılık eleştirisi:</strong> CBAM, gelişmekte olan
              ülkelerin rekabet gücünü kırabilir. AB'nin karbon fiyatını
              kendi üretim yapısına göre belirleyip bunu ithalata dayatması,
              Küresel Güney açısından tek taraflı bir standart ihracıdır.
              "Ortak ama farklılaştırılmış sorumluluklar" ilkesiyle gerilim
              içindedir.
            </li>
            <li>
              <strong>Büyüme çelişkisi:</strong> Mutabakat kendini "yeni
              büyüme stratejisi" olarak sunar. Ama yeşil büyüme varsayımı —
              büyüme ile kaynak tüketiminin mutlak ayrışması — ampirik olarak
              kanıtlanmış değil. SDG çerçevesindeki aynı çelişki için:{" "}
              <Link to="/yazilar/sdg-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                SDGs Rehberi
              </Link>.
            </li>
          </ol>
        </section>

        <section id="ne-yapmali" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            8. Ne yapmalı: okuma önerisi
          </h2>
          <p>
            Yeşil Mutabakat'ı okumanın sağlıklı yolu, onu ne kurtarıcı bir
            plan ne de bir kandırmaca olarak görmekten geçer. O, bir kıtanın
            kendi sanayi modelini iklim gerçeğiyle uyumlu hale getirme
            denemesidir — eksik, adaletsiz ve çelişkili, ama bağlayıcı.
            Bağlayıcılığı, onu Paris Anlaşması'ndan ayıran en önemli
            özelliktir.
          </p>
          <p>
            Türkiye açısından doğru soru "CBAM'a nasıl uyum sağlarız?" değil,
            "bu dönüşümü kendi kalkınma gündemimiz için nasıl fırsata
            çeviririz?" olmalıdır. Temiz enerji yatırımı, yeşil sanayi
            politikası ve şeffaf karbon muhasebesi — uyum maliyeti olarak
            değil, rekabet stratejisi olarak okunduğunda anlam kazanır.
          </p>
        </section>

        <section id="sss" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            9. Sıkça sorulanlar
          </h2>
          <div className="space-y-6 mt-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-serif text-lg md:text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-ink/80">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-ink/10 pt-10">
          <h2 className="font-serif text-2xl tracking-tight mb-4 text-ink">Devamı için</h2>
          <ul className="space-y-3 text-base">
            <li>
              →{" "}
              <Link to="/yazilar/sdg-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                SDGs Rehberi
              </Link>{" "}
              — küresel hedefler çerçevesinin eleştirel okuması.
            </li>
            <li>
              →{" "}
              <Link to="/yazilar/greenwashing-nedir" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                Greenwashing Nedir?
              </Link>{" "}
              — yeşil iddiaların sınanması.
            </li>
            <li>
              →{" "}
              <Link to="/cop31" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                COP31 Analiz Katmanı
              </Link>{" "}
              — iklim finansmanı ve karbon piyasaları üzerine notlar.
            </li>
          </ul>
        </section>
      </div>
        </article>
      </PageSection>
    </>
  );
}
